<template>
  <b-modal
    v-model="corsForm"
    centered
    size="lg"
    :scrollable="true"
    cancel-variant="bg-white"
    :ok-title="editing ? 'Save' : 'Add'"
    :title="editing ? 'Edit Origin Settings' : 'Add Origin'"
    body-class="action-modal"
    :busy="addingCorsSettings || updatingCorsSettings"
    @ok.prevent="submitCors"
  >
    <div v-if="ready">
      <validation-observer ref="cors-validation">
        <b-row>
          <b-col>
            <validation-provider
              #default="{ errors }"
              name="Origin"
              rules="required"
            >
              <input-field
                name="cors-origin"
                label="Origin *"
                label-position="top"
                :error="errors.length ? errors[0] : null"
              >
                <b-form-input
                  id="cors-origin"
                  v-model="corsSettings.origin"
                  placeholder="Enter origin here..."
                  :state="errors.length > 0 ? false : null"
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <validation-provider name="Allowed Methods">
              <input-field
                name="cors-origin-methods"
                label="Allowed Methods"
                label-position="top"
              >
                <b-form-checkbox-group
                  id="cors-allowed-methods"
                  v-model="corsSettings.allowedMethods"
                  class="d-md-flex"
                  :options="methodOptions"
                  name="cors-allowed-methods"
                />
              </input-field>
            </validation-provider>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <input-field
              name="cors-origin-headers"
              label="Allowed Headers"
              label-position="top"
            >
              <div class="d-flex flex-column w-full">
                <span
                  v-if="!corsSettings.allowedHeaders.length"
                  class="text-secondary"
                >
                  No headers
                </span>
                <div
                  v-for="header in corsSettings.allowedHeaders"
                  :key="`header-${header.id}`"
                  class="d-flex align-items-center mb-1"
                >
                  <b-form-input
                    id="create_job-input"
                    v-model="header.name"
                    placeholder="Enter header name here..."
                  />
                  <span
                    class="ml-1 cursor-pointer"
                    @click="removeHeader(header.id)"
                  >
                    <GjIcon name="Delete" />
                  </span>
                </div>
                <div class="mt-1">
                  <a
                    class="text-primary text-medium"
                    @click="addHeader"
                  >
                    + Add Header
                  </a>
                </div>
              </div>
            </input-field>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <input-field
              name="cors-origin-expose-headers"
              label="Expose Headers"
              label-position="top"
            >
              <div class="d-flex flex-column w-full">
                <span
                  v-if="!corsSettings.exposeHeaders.length"
                  class="text-secondary"
                >
                  No expose headers
                </span>
                <div
                  v-for="header in corsSettings.exposeHeaders"
                  :key="`expose-header-${header.id}`"
                  class="d-flex align-items-center mb-1"
                >
                  <b-form-input
                    id="create_job-input"
                    v-model="header.name"
                    placeholder="Enter header name here..."
                  />
                  <span
                    class="ml-1 cursor-pointer"
                    @click="removeExposeHeader(header.id)"
                  >
                    <GjIcon name="Delete" />
                  </span>
                </div>
                <div class="mt-1">
                  <a
                    class="text-primary text-medium"
                    @click="addExposeHeader"
                  >
                    + Add Expose Header
                  </a>
                </div>
              </div>
            </input-field>
          </b-col>
        </b-row>
      </validation-observer>
    </div>
    <div
      v-else
      class="text-center py-3"
    >
      <b-spinner />
    </div>
  </b-modal>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import { v4 as uuidv4 } from 'uuid'

const emptyModel = {
  origin: '',
  maxAge: 0,
  allowedHeaders: [],
  allowedMethods: [],
  exposeHeaders: [],
}
export default {
  name: 'CorsForm',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Object,
      default: null,
    },
    accountId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      required,
      ready: false,
      corsForm: false,
      bucketId: null,
      corsSettings: {
        origin: '',
        maxAge: 0,
        allowedHeaders: [],
        allowedMethods: [],
        exposeHeaders: [],
      },
      methodOptions: [
        { text: 'GET', value: 'GET' },
        { text: 'PUT', value: 'PUT' },
        { text: 'POST', value: 'POST' },
        { text: 'DELETE', value: 'DELETE' },
        { text: 'HEAD', value: 'HEAD' },
      ],
    }
  },
  computed: {
    updatingCorsSettings() {
      return this.$store.state.accountBucketsCors.updatingCorsSettings
    },
    addingCorsSettings() {
      return this.$store.state.accountBucketsCors.addingCorsSettings
    },
    bucketName() {
      return this.$route.params.id
    },
    accountid() {
      return this.$route.params.accountId
    },
  },
  watch: {
    corsForm(val) {
      this.$emit('input', val)
      if (val) {
        if (this.editing) {
          this.getCorsById()
        } else {
          this.ready = true
          this.corsSettings = emptyModel
        }
      }
    },
    value(val) {
      this.corsForm = val
    },
  },
  created() {
    this.bucketId = this.$route.params.id
    this.corsForm = this.value
  },
  methods: {
    getCorsById() {
      this.ready = false
      const query = {
        accountId: this.accountid,
        bucketName: this.bucketName,
        corsId: this.editing.id,
      }

      this.$store
        .dispatch('accountBucketsCors/fetchCorsSettingsById', query)
        .then(res => {
          const fetchedSettings = res.data.result
          this.corsSettings = {
            ...fetchedSettings,
            allowedHeaders: fetchedSettings.allowedHeaders.map(header => ({
              name: header,
              id: uuidv4(),
            })),
            exposeHeaders: fetchedSettings.exposeHeaders.map(header => ({
              name: header,
              id: uuidv4(),
            })),
          }
          this.ready = true
        })
    },
    submitCors() {
      const cors = {
        ...this.corsSettings,
        allowedHeaders: this.corsSettings.allowedHeaders
          .map(h => h.name)
          .filter(val => val !== ''),
        exposeHeaders: this.corsSettings.exposeHeaders
          .map(h => h.name)
          .filter(val => val !== ''),
      }
      // eslint-disable-next-line no-unused-expressions
      this.editing ? this.updateCors(cors) : this.addCors(cors)
    },
    addCors(cors) {
      const query = {
        accountId: this.accountId,
        bucketName: this.bucketId,
        cors,
      }

      this.$store
        .dispatch('accountBucketsCors/addCorsSettings', query)
        .then(() => {
          this.successToast('Cors origin successfully added!')
          this.$emit('fetchSettings')
          this.clearForm()
        })
        .catch(err => {
          this.errorToast(err.response?.data?.errors[0] || 'Something went wrong while adding Cors origin!')
        })
    },
    clearForm() {
      this.corsSettings.origin = ''
      this.corsSettings.maxAge = 0
      this.corsSettings.allowedHeaders = []
      this.corsSettings.allowedMethods = []
      this.corsSettings.exposeHeaders = []
    },
    updateCors(cors) {
      const query = {
        accountId: this.accountId,
        bucketName: this.bucketId,
        cors,
      }

      this.$store
        .dispatch('accountBucketsCors/updateCorsSettings', query)
        .then(() => {
          this.successToast('Cors origin successfully updated!')
          this.$emit('fetchSettings')
        })
        .catch(err => {
          this.errorToast(err.response?.data?.errors[0] || 'Something went wrong while updating Cors origin!')
        })
    },
    addHeader() {
      this.corsSettings.allowedHeaders.push({
        name: '',
        id: uuidv4(),
      })
    },
    removeHeader(id) {
      this.corsSettings.allowedHeaders = this.corsSettings.allowedHeaders.filter(
        h => h.id !== id,
      )
    },
    removeExposeHeader(id) {
      this.corsSettings.exposeHeaders = this.corsSettings.exposeHeaders.filter(
        h => h.id !== id,
      )
    },
    addExposeHeader() {
      this.corsSettings.exposeHeaders.push({
        name: '',
        id: uuidv4(),
      })
    },
  },
}
</script>

<style>
/* #cors-allowed-methods {
  display: flex;
} */
#cors-allowed-methods > .custom-control {
  margin-bottom: 6px;
  margin-right: 20px;
}
</style>
